import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Swal from "../../utils/apparifySwal";
import ThemeToggle from '../../components/ThemeToggle';
import PasswordInput from '../../components/PasswordInput';
import { getLoginErrorDialogOptions } from '../../utils/loginError';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const inputClassName = 'h-12 w-full rounded-xl border border-neutral-300 bg-neutral-50 px-4 text-sm text-neutral-900 outline-none transition-colors placeholder:text-neutral-400 focus:border-neutral-500 focus:ring-2 focus:ring-neutral-200 dark:border-neutral-700 dark:bg-neutral-950/40 dark:text-neutral-100 dark:focus:border-neutral-500 dark:focus:ring-neutral-700';

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    Swal.fire({
      title: 'Signing in...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const { user } = await login({ email, password }); 
      // Note: passing 'email' as key since Login.jsx does so. 
      // If backend supports username login via 'email' field, it's fine.
      
      if (user && (user.role === 'ADMIN' || user.role === 'MODERATOR')) {
          await Swal.fire({
            icon: 'success',
            title: 'Welcome!',
            html: 'You are now signed in.',
            timer: 1200,
            showConfirmButton: false,
          });
          navigate('/admin/dashboard');
      } else {
          await logout();
          Swal.close();
          await Swal.fire({
            ...getLoginErrorDialogOptions(null, 'You do not have permission to access the Admin Portal.'),
          });
      }
    } catch (error) {
      console.error('Login error:', error);
      Swal.close();
      await Swal.fire({
        ...getLoginErrorDialogOptions(error),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative isolate flex min-h-screen flex-col justify-center bg-[#f4f3ef] px-4 py-24 text-neutral-900 dark:bg-[#101011] dark:text-neutral-50 sm:px-6 lg:px-8">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-6 right-[4.75rem] -z-10 max-w-[calc(100%_-_5.75rem)] select-none text-right text-[clamp(1.25rem,4vw,4.5rem)] font-black leading-none tracking-[-0.07em] text-neutral-900/[0.13] dark:text-white/[0.05] sm:bottom-7 sm:right-[5.25rem]"
      >
        BEHIND THE STOREFRONT.
      </div>
      <ThemeToggle
        variant="icon"
        className="fixed bottom-5 right-5 z-50 sm:bottom-6 sm:right-6"
      />

      <div className="mx-auto w-full max-w-[440px]">
        <div className="overflow-hidden rounded-2xl border border-neutral-300 bg-white shadow-[0_16px_48px_-28px_rgba(0,0,0,0.25)] dark:border-neutral-700 dark:bg-neutral-900 dark:shadow-none">
          <div className="flex items-center justify-between border-b border-neutral-200 bg-neutral-50 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-500 dark:border-neutral-800 dark:bg-neutral-950/40 dark:text-neutral-400 sm:px-8">
            <span>Apparify</span>
            <span>Admin access</span>
          </div>
          <div className="px-6 pb-8 pt-7 sm:px-9 sm:pb-9">
          <div className="mb-7 text-center">
            <img
              src={`${process.env.PUBLIC_URL}/apparify-icon.png`}
              alt="Apparify"
              className="mx-auto h-14 w-14 rounded-xl object-contain"
            />
            <h2 className="mt-4 text-center text-3xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
              Admin Portal
            </h2>
          </div>
          <form className="space-y-6" onSubmit={handleLogin}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Email address / Username
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  name="email"
                  type="text"
                  autoComplete="email"
                  placeholder="Enter your email or username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClassName}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Password
              </label>
              <div className="mt-2">
                <PasswordInput
                  id="password"
                  name="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClassName}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex min-h-12 w-full items-center justify-center rounded-xl bg-neutral-950 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-neutral-100 dark:text-neutral-950 dark:hover:bg-white dark:focus-visible:ring-offset-neutral-900"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
